import React, { useRef, useEffect, useState, useCallback } from 'react';
import { getCoords } from '#utils';
import styles from './HorizontalScrollList.module.scss';
import PosterTeaser from '#components/teasers/poster-teaser/PosterTeaser';
import { useOvermindActions } from '#store/overmind';
import { ContinueWatchingItem } from '#store/namespaces/continue-watching/state';
import { getTeaserDetailsPosition } from '#utils';
import IconButton from '#components/controls/icon-button/IconButton';
import ChevronLeft from '#components/icons/ChevronLeft';
import ChevronRight from '#components/icons/ChevronRight';
import { useSpring } from '@react-spring/web';
import { TEASER_HOVER_DELAY } from '#config/constants';
import {
  useInputDeviceInfo,
  useWindowDimensions,
  useMutationObserver,
} from '#hooks';
import { Teaser } from '#types/generated/graphql-types';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';
import { debounce } from 'lodash';

export interface ITeaserRenderer {
  teaserDataRaw: Teaser;
  continueWatchingState?: ContinueWatchingItem;
  index?: number;
  extraClass?: string;
  onMouseEnter?: (event: React.MouseEvent, data: Teaser) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
}

interface Props<T> {
  items: T[];
  direction?: 'portrait' | 'landscape';
  TeaserComponent?: React.FC<ITeaserRenderer>;
  renderItem?: (item: T, index: number) => JSX.Element;
  onEndReached?: () => void;
  isLoadingMoreItems?: boolean;
  teasersShape?: 'square';
}

const HorizontalScrollList = <T extends Record<string, unknown>>({
  direction = 'portrait',
  TeaserComponent = PosterTeaser,
  items,
  renderItem,
  onEndReached,
  isLoadingMoreItems,
  teasersShape,
}: Props<T>) => {
  const { showDetails } = useOvermindActions().teaserDetails;
  const { isTouchOnly } = useInputDeviceInfo();
  const { dimensions } = useWindowDimensions();
  const [pageSize, setPageSize] = useState(0);
  const [leftButtonVisible, setLeftButtonVisible] = useState(false);
  const [rightButtonVisible, setRightButtonVisible] = useState(false);
  const [, setX] = useSpring(() => ({ x: 0 }));
  const [prevScrollViewScrollPosition, setPrevScrollViewScrollPosition] =
    useState(0);

  const hoverDelay = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef();
  const scrollViewRef = useRef<HTMLUListElement>();

  const updateView = () => {
    if (!scrollViewRef.current) return;

    const visibleColumns = parseFloat(
      getComputedStyle(scrollViewRef.current).getPropertyValue(
        '--visible-columns'
      )
    );

    const fullColumnWidth = parseFloat(
      getComputedStyle(scrollViewRef.current).getPropertyValue(
        '--full-column-width'
      )
    );

    const columnGap = parseFloat(
      getComputedStyle(scrollViewRef.current).getPropertyValue(
        '--grid-column-gap'
      )
    );

    const listWidth = getCoords(scrollViewRef.current).width;
    const itemWidth = listWidth / visibleColumns;
    const scrollDistance =
      ((itemWidth * visibleColumns + columnGap) / 100) * fullColumnWidth;
    const scrollWidth = scrollViewRef.current.scrollWidth - 1;
    const { scrollLeft } = scrollViewRef.current;
    setPageSize(scrollDistance);
    setLeftButtonVisible(scrollLeft > 0);

    const isNotEnd =
      scrollWidth > listWidth && scrollLeft < scrollWidth - listWidth;
    setRightButtonVisible(isNotEnd);

    if (!isNotEnd) {
      onEndReached?.();
    }
  };

  const handleMutation = useCallback(() => {
    updateView();
  }, [items, dimensions]);

  useMutationObserver(scrollViewRef.current, handleMutation);

  /** init buttons **/
  useEffect(() => {
    updateView();
  }, [items, dimensions]);

  /* clear delay on unmount */
  useEffect(() => {
    return () => {
      if (hoverDelay.current) {
        clearTimeout(hoverDelay.current);
      }
    };
  }, []);

  const handleTeaserMouseEnter = (event: React.MouseEvent, itemData) => {
    if (isTouchOnly) return;
    const target = event.target as HTMLElement;
    const { centerX, centerY, right, left } = getTeaserDetailsPosition(target);
    const { right: containerRight, left: containerLeft } = getCoords(
      containerRef.current
    );

    if (hoverDelay.current) {
      clearTimeout(hoverDelay.current);
    }

    if (left >= containerLeft && right <= containerRight) {
      hoverDelay.current = setTimeout(() => {
        showDetails({
          data: itemData,
          position: { top: centerY, left: centerX },
        });
      }, TEASER_HOVER_DELAY);
    }
  };

  const handleTeaserMouseLeave = () => {
    if (hoverDelay.current) {
      clearTimeout(hoverDelay.current);
      hoverDelay.current = null;
    }
  };

  const scroll = (scrollDirection = 1) => {
    if (scrollViewRef.current) {
      const { width } = getCoords(containerRef.current);
      const currentPos = scrollViewRef.current.scrollLeft;
      const targetPos = currentPos + pageSize * scrollDirection;

      setX({
        x: targetPos,
        reset: true,
        from: { x: currentPos },
        config: { duration: 300 },
        onChange: (props) => {
          scrollViewRef.current.scrollTo({ left: props.value.x });
        },
        onRest: () => {
          updateView();
        },
      });

      setLeftButtonVisible(targetPos > 0);
      setRightButtonVisible(
        targetPos < scrollViewRef.current.scrollWidth - width
      );
    }
  };

  const scrollLeft = () => {
    scroll(-1);
  };

  const scrollRight = () => {
    scroll();
  };

  const handleLeftButtonKeyEvent = (
    event: React.KeyboardEvent<HTMLElement>
  ) => {
    if (event.key === 'Enter') {
      scrollLeft();
    }
  };

  const handleRightButtonKeyEvent = (
    event: React.KeyboardEvent<HTMLElement>
  ) => {
    if (event.key === 'Enter') {
      scrollRight();
    }
  };

  const handleTouchListScroll = debounce(() => {
    if (!isTouchOnly) return;

    const currentScrollLeft = scrollViewRef.current.scrollLeft;
    const isScrollingRight = currentScrollLeft > prevScrollViewScrollPosition;

    if (isScrollingRight) {
      const listWidth = getCoords(scrollViewRef.current).width;
      const scrollWidth = scrollViewRef.current.scrollWidth;
      const isNotEnd =
        scrollWidth > listWidth && currentScrollLeft < scrollWidth - listWidth;

      if (!isNotEnd) {
        onEndReached?.();
      }
    }

    setPrevScrollViewScrollPosition(currentScrollLeft);
  }, 100);

  const renderItemComponent = (item, index) => {
    if (renderItem) {
      return renderItem(item, index);
    }

    return (
      <TeaserComponent
        teaserDataRaw={item}
        index={index}
        onMouseEnter={handleTeaserMouseEnter}
        onMouseLeave={() => handleTeaserMouseLeave()}
      />
    );
  };

  const renderItems = () => {
    return items.map((item, index) => {
      return (
        <li key={typeof item === 'string' ? item : String(item?.id)}>
          {renderItemComponent(item, index)}
        </li>
      );
    });
  };

  return (
    <div ref={containerRef} className={styles.host}>
      <ul
        ref={scrollViewRef}
        className={`${styles.listContainer} ${
          direction === 'portrait'
            ? styles.listContainerPortrait
            : styles.listContainerLandscape
        } ${teasersShape === 'square' && styles.landscapeSquareItems}`}
        onScroll={handleTouchListScroll}
      >
        {renderItems()}

        {!isTouchOnly && leftButtonVisible && (
          <div
            tabIndex={-1}
            className={styles.leftButton}
            role="button"
            onClick={scrollLeft}
            onKeyDown={handleLeftButtonKeyEvent}
          >
            <IconButton size="large">
              <ChevronLeft size={35} />
            </IconButton>
          </div>
        )}
        {!isLoadingMoreItems && !isTouchOnly && rightButtonVisible && (
          <div
            tabIndex={-1}
            className={styles.rightButton}
            role="button"
            onClick={scrollRight}
            onKeyDown={handleRightButtonKeyEvent}
          >
            <IconButton size="large">
              <ChevronRight size={35} />
            </IconButton>
          </div>
        )}
        {isLoadingMoreItems && (
          <div tabIndex={-1} className={styles.rightLoader} role="button">
            <CircularProgress />
          </div>
        )}
      </ul>
    </div>
  );
};

export default HorizontalScrollList;
